import { create } from 'zustand';
import {
  getDirections,
  getTransitRoute,
  type Route,
  type TransitProfile,
  type TravelMode,
  type Coordinates
} from '../services/navigation';
import { canRequestDirectionsInGhana } from '../utils/regionAvailability';

/** Road geometry is stable; 10 min avoids repeat Valhalla calls within a session. */
export const DIRECTIONS_CACHE_TTL_MS = 10 * 60 * 1000;
const DIRECTIONS_CACHE_MAX_ENTRIES = 8;
/** ~11 m — absorbs GPS jitter without treating nearby fixes as new routes. */
const COORD_CACHE_DECIMALS = 4;

interface DirectionsLocation {
  coordinates: Coordinates;
  name: string;
  address?: string;
}

interface FetchDirectionsOptions {
  /** Bypass in-flight dedup and response cache (e.g. manual refresh). */
  force?: boolean;
}

interface DirectionsCacheEntry {
  routes: Route[];
  transitProfile: TransitProfile | null;
  fetchedAt: number;
}

interface DirectionsStore {
  // Route state
  origin: DirectionsLocation | null;
  destination: DirectionsLocation | null;
  routes: Route[];
  selectedRouteIndex: number;
  transitProfile: TransitProfile | null;

  // UI state
  isLoading: boolean;
  error: string | null;
  travelMode: TravelMode;
  showAlternatives: boolean;
  showSteps: boolean;

  // Options
  avoidTolls: boolean;
  avoidHighways: boolean;
  avoidFerries: boolean;

  // Actions
  setOrigin: (location: DirectionsLocation | null) => void;
  setDestination: (location: DirectionsLocation | null) => void;
  swapLocations: () => void;
  setTravelMode: (mode: TravelMode) => void;
  setSelectedRouteIndex: (index: number) => void;
  setShowAlternatives: (show: boolean) => void;
  setShowSteps: (show: boolean) => void;
  setAvoidTolls: (avoid: boolean) => void;
  setAvoidHighways: (avoid: boolean) => void;
  setAvoidFerries: (avoid: boolean) => void;

  // API actions
  fetchDirections: (options?: FetchDirectionsOptions) => Promise<void>;
  clearDirections: () => void;
  clearTransitProfile: () => void;

  // Computed
  selectedRoute: () => Route | null;
}

const FETCH_DEBOUNCE_MS = 50;

let fetchDebounceTimer: ReturnType<typeof setTimeout> | null = null;
let directionsAbortController: AbortController | null = null;
let inFlightFetchKey: string | null = null;
const directionsCache = new Map<string, DirectionsCacheEntry>();

function roundCoord(value: number): number {
  const factor = 10 ** COORD_CACHE_DECIMALS;
  return Math.round(value * factor) / factor;
}

function normalizeCoordinates(coords: Coordinates): Coordinates {
  return { lat: roundCoord(coords.lat), lng: roundCoord(coords.lng) };
}

function buildFetchKey(state: DirectionsStore): string | null {
  const { origin, destination, travelMode, avoidTolls, avoidHighways, avoidFerries } = state;
  if (!origin || !destination) return null;

  return JSON.stringify({
    origin: normalizeCoordinates(origin.coordinates),
    destination: normalizeCoordinates(destination.coordinates),
    travelMode,
    avoidTolls,
    avoidHighways,
    avoidFerries,
  });
}

function getCachedDirections(fetchKey: string): DirectionsCacheEntry | null {
  const entry = directionsCache.get(fetchKey);
  if (!entry) return null;

  if (Date.now() - entry.fetchedAt > DIRECTIONS_CACHE_TTL_MS) {
    directionsCache.delete(fetchKey);
    return null;
  }

  return entry;
}

function setCachedDirections(
  fetchKey: string,
  routes: Route[],
  transitProfile: TransitProfile | null,
) {
  if (directionsCache.has(fetchKey)) {
    directionsCache.delete(fetchKey);
  }

  directionsCache.set(fetchKey, {
    routes,
    transitProfile,
    fetchedAt: Date.now(),
  });

  while (directionsCache.size > DIRECTIONS_CACHE_MAX_ENTRIES) {
    const oldestKey = directionsCache.keys().next().value;
    if (oldestKey === undefined) break;
    directionsCache.delete(oldestKey);
  }
}

function clearDirectionsResponseCache() {
  directionsCache.clear();
}

/** Clears in-memory response cache (tests only). */
export function resetDirectionsCacheForTests() {
  clearDirectionsResponseCache();
  inFlightFetchKey = null;
  directionsAbortController = null;
  cancelScheduledFetch();
}

function cancelScheduledFetch() {
  if (fetchDebounceTimer) {
    clearTimeout(fetchDebounceTimer);
    fetchDebounceTimer = null;
  }
}

function abortInFlightFetch() {
  directionsAbortController?.abort();
  directionsAbortController = null;
  inFlightFetchKey = null;
}

function cancelPendingDirectionsFetch() {
  cancelScheduledFetch();
  abortInFlightFetch();
}

function scheduleFetchDirections(get: () => DirectionsStore) {
  if (fetchDebounceTimer) {
    clearTimeout(fetchDebounceTimer);
  }

  fetchDebounceTimer = setTimeout(() => {
    fetchDebounceTimer = null;
    void get().fetchDirections();
  }, FETCH_DEBOUNCE_MS);
}

export const useDirectionsStore = create<DirectionsStore>((set, get) => ({
  // Initial state
  origin: null,
  destination: null,
  routes: [],
  selectedRouteIndex: 0,
  transitProfile: null,

  isLoading: false,
  error: null,
  travelMode: 'driving',
  showAlternatives: true,
  showSteps: false,

  avoidTolls: false,
  avoidHighways: false,
  avoidFerries: false,

  // Actions
  setOrigin: (location) => {
    set({ origin: location, error: null });
    if (location && get().destination) {
      scheduleFetchDirections(get);
    }
  },

  setDestination: (location) => {
    set({ destination: location, error: null });
    if (location && get().origin) {
      scheduleFetchDirections(get);
    }
  },

  swapLocations: () => {
    const { origin, destination } = get();
    set({ origin: destination, destination: origin });
    if (origin && destination) {
      scheduleFetchDirections(get);
    }
  },

  setTravelMode: (mode) => {
    set({ travelMode: mode });
    if (get().origin && get().destination) {
      scheduleFetchDirections(get);
    }
  },

  setSelectedRouteIndex: (index) => set({ selectedRouteIndex: index }),
  setShowAlternatives: (show) => set({ showAlternatives: show }),
  setShowSteps: (show) => set({ showSteps: show }),

  setAvoidTolls: (avoid) => {
    set({ avoidTolls: avoid });
    if (get().origin && get().destination) {
      scheduleFetchDirections(get);
    }
  },

  setAvoidHighways: (avoid) => {
    set({ avoidHighways: avoid });
    if (get().origin && get().destination) {
      scheduleFetchDirections(get);
    }
  },

  setAvoidFerries: (avoid) => {
    set({ avoidFerries: avoid });
    if (get().origin && get().destination) {
      scheduleFetchDirections(get);
    }
  },

  fetchDirections: async (options = {}) => {
    cancelScheduledFetch();

    const state = get();
    const fetchKey = buildFetchKey(state);

    if (!fetchKey) {
      set({ error: 'Please set both origin and destination' });
      return;
    }

    const { origin, destination } = state;
    if (!canRequestDirectionsInGhana(origin, destination)) {
      abortInFlightFetch();
      inFlightFetchKey = null;
      set({
        routes: [],
        selectedRouteIndex: 0,
        isLoading: false,
        error: null,
      });
      return;
    }

    if (!options.force && inFlightFetchKey === fetchKey) {
      return;
    }

    if (!options.force) {
      const cached = getCachedDirections(fetchKey);
      if (cached) {
        const { selectedRouteIndex, routes: currentRoutes } = get();
        const preserveSelection =
          currentRoutes.length > 0 && currentRoutes.length === cached.routes.length;

        set({
          routes: cached.routes,
          transitProfile: cached.transitProfile,
          selectedRouteIndex: preserveSelection
            ? Math.min(selectedRouteIndex, cached.routes.length - 1)
            : 0,
          isLoading: false,
          error: null,
        });
        return;
      }
    }

    abortInFlightFetch();

    const { travelMode, avoidTolls, avoidHighways, avoidFerries } = state;
    const abortController = new AbortController();
    directionsAbortController = abortController;
    inFlightFetchKey = fetchKey;

    set({ isLoading: true, error: null });

    try {
      if (travelMode === 'transit') {
        const response = await getTransitRoute(
          origin!.coordinates,
          destination!.coordinates,
          abortController.signal
        );

        if (abortController.signal.aborted) return;

        set({
          routes: [response.route],
          transitProfile: response.transit_profile,
          selectedRouteIndex: 0,
          isLoading: false,
        });
        setCachedDirections(fetchKey, [response.route], response.transit_profile);
      } else {
        const response = await getDirections(
          origin!.coordinates,
          destination!.coordinates,
          {
            mode: travelMode,
            alternatives: true,
            steps: true,
            avoidTolls,
            avoidHighways,
            avoidFerries,
            signal: abortController.signal,
          }
        );

        if (abortController.signal.aborted) return;

        set({
          routes: response.routes,
          transitProfile: null,
          selectedRouteIndex: 0,
          isLoading: false,
        });
        setCachedDirections(fetchKey, response.routes, null);
      }
    } catch (error) {
      if (abortController.signal.aborted) {
        return;
      }

      set({
        error: error instanceof Error ? error.message : 'Failed to get directions',
        isLoading: false,
        routes: [],
      });
    } finally {
      if (directionsAbortController === abortController) {
        directionsAbortController = null;
      }
      if (inFlightFetchKey === fetchKey) {
        inFlightFetchKey = null;
      }
    }
  },

  clearDirections: () => {
    cancelPendingDirectionsFetch();
    set({
      origin: null,
      destination: null,
      routes: [],
      transitProfile: null,
      selectedRouteIndex: 0,
      error: null,
      isLoading: false,
    });
  },

  clearTransitProfile: () => set({ transitProfile: null }),

  selectedRoute: () => {
    const { routes, selectedRouteIndex } = get();
    return routes[selectedRouteIndex] || null;
  },
}));
